import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import {
  AuthenticatedTemplate, UnauthenticatedTemplate,
  useIsAuthenticated, useMsal,
} from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { apiScopes } from '@/auth/msalConfig'
import { configureTokenGetter } from '@/api/client'
import { SignInLanding, OidcRedirect } from '@/components/SignInShells'

/**
 * Wraps the app and:
 *  - Renders SignInLanding when no account is signed in (user clicks "Sign
 *    in with Microsoft" → loginRedirect to Entra).
 *  - Once signed in, configures the api/client's bearer-token getter so
 *    every API call carries a fresh access token (silent acquireToken,
 *    falling back to redirect when interactive consent is needed).
 *  - Shows the OIDC-redirect splash during the round-trip.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [tokenReady, setTokenReady] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !accounts[0]) return
    const account = accounts[0]
    // Token getter: every apiFetch call resolves a fresh bearer.
    configureTokenGetter(async () => {
      try {
        const r = await instance.acquireTokenSilent({ scopes: apiScopes, account })
        return r.accessToken
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes: apiScopes, account })
          return null
        }
        // eslint-disable-next-line no-console
        console.error('acquireTokenSilent failed', e)
        return null
      }
    })
    setTokenReady(true)
    // Hand off to the v3 design SPA at /console/. We stash the bearer +
    // signed-in account in sessionStorage so design's store.js can pick
    // them up and call /api/* with the same identity. One-shot acquire
    // so the design page lands with a valid token (it'll re-fetch from
    // configureTokenGetter on its own thereafter).
    //
    // ?redirect=<encoded-url> supports silent refresh: when design's
    // store.js sees a 401, it sends the user to /?redirect=/console/...
    // so MSAL can quietly acquire a fresh token (sessionStorage cache hit)
    // and ship the user back to where they were. Default destination is
    // the design's index.html.
    ;(async () => {
      try {
        const r = await instance.acquireTokenSilent({ scopes: apiScopes, account })
        sessionStorage.setItem('fusion-bearer', r.accessToken)
        sessionStorage.setItem('fusion-account', JSON.stringify({
          upn: account.username,
          oid: account.localAccountId,
          name: account.name ?? account.username,
          tenant: account.tenantId,
        }))
        const params = new URLSearchParams(window.location.search)
        const redirect = params.get('redirect')
        // Only allow same-origin same-app paths to avoid open-redirect.
        const safe = redirect && redirect.startsWith('/console/') ? redirect : '/console/index.html'
        window.location.replace(safe)
      } catch (e) {
        console.error('handoff to /console/ failed', e)
      }
    })()
  }, [isAuthenticated, accounts, instance])

  if (inProgress !== 'none' && inProgress !== 'handleRedirect') {
    return <OidcRedirect />
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <SignInLanding onSignIn={() => instance.loginRedirect({ scopes: apiScopes })} />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        {tokenReady ? children : <OidcRedirect />}
      </AuthenticatedTemplate>
    </>
  )
}
